import { REQUESTABLE_SUBMODULES_STATUS } from '@utils/types/RequestedDashboards';
import apiRoutes from '../constants/apiRoutes';
import baseApi from '../services/api';
import { SubmoduleCategory } from '@constants/constants';
import { ApiResponse } from './types/common';
import { SubmoduleFiltersResponse } from './types/filters';
import { UserCompany } from './types/visualization';

interface GetChartsSpecificFilterValuesApiResponse {
  code: number;
  message: string;
  data: {
    result: Record<string, FieldData>;
  };
}

interface FieldData {
  name: string;
  displayName: string;
  field: string;
  metadata: unknown | null;
  values: string[];
}

export interface UserSubModulesFilterValueApiMap {
  id: string;
  filterValue: string;
}

export interface UserSubModulesFilterApiMap {
  id: string;
  name: string;
  submoduleFilterRoles: UserSubModulesFilterValueApiMap[];
}

export interface UserSubModulesApiMap {
  id: string;
  name: string;
  url: string;
  embedId: string;
  submoduleType: string;
  submoduleFilters: UserSubModulesFilterApiMap[];
}

export interface NestedUserModulesApiMap {
  code: number;
  message: string;
  data: Data;
}

export interface Data {
  modules: Module[];
}

export interface ProjectedSubmodule {
  name: string;
  url: string;
  submoduleType: string;
}

export interface LinkedSubmodule extends ProjectedSubmodule {
  linkedSubmoduleType: string;
}

export interface Module {
  id: string;
  name: string;
  sortOrder: string;
  metadata?: string;
  submoduleCatalogs: SubmoduleCatalog[];
}

export interface SubmoduleCatalog {
  id: string;
  name: string;
  url: string;
  embedId?: string;
  defaultFilter?: string;
  nativeFilterId: any;
  submoduleType?: string;
  submoduleCategory: SubmoduleCategory;
  hasFullAccess?: boolean;
}

export interface RequestedSubModule {
  status: REQUESTABLE_SUBMODULES_STATUS;
  requestId: string;
  submoduleName: string;
  submoduleCatalogId: string;
  moduleName: string;
  moduleMetadata: string;
}

// export interface RequestedSubModulesApiMap {
//   code: number;
//   message: string;
//   data: RequestedSubModule[];
// }

export interface RequestSubModuleApiMap {
  code: number;
  message: string;
  data: RequestedSubModule[];
}

const modules = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getModules: builder.query<NestedUserModulesApiMap, void>({
      query: () => `${apiRoutes.userModules}`,
    }),
    getSubModules: builder.query<any, string>({
      query: (moduleId) => apiRoutes.userSubmodules(moduleId),
    }),
    getSubModuleFilters: builder.query<any, string>({
      query: (submoduleId) => apiRoutes.userSubmoduleFilters(submoduleId),
      keepUnusedDataFor: 300,
    }),
    getUserModulesById: builder.query<any, string>({
      query: (id) => apiRoutes.userModulesById(id),
    }),
    getGuestToken: builder.query<any, string>({
      query: (submoduleId) => apiRoutes.userGuestToken(submoduleId),
    }),
    addFilterValues: builder.mutation<any, any>({
      query: (payload: {
        moduleId: string;
        submoduleId: string;
        filterId: string;
        values: string[];
      }) => ({
        url: apiRoutes.userSubmoduleFiltersValues(payload.submoduleId, payload.filterId),
        method: 'POST',
        body: {
          values: payload.values,
        },
      }),
    }),
    getCustomDashboardFilters: builder.query<ApiResponse<SubmoduleFiltersResponse>, string>({
      query: (submoduleId) => apiRoutes.customDashboardFilters(submoduleId),
    }),
    getUserModuleGames: builder.query<any, any>({
      query: (payload: { submoduleId: string; gamesParam: string[] }) =>
        apiRoutes.userSubmoduleGames(payload.submoduleId, payload.gamesParam),
    }),

    // get companies allowed for the user for a submodule
    getUserModuleCompanies: builder.query<ApiResponse<UserCompany[]>, { submoduleId: string }>({
      query: (payload: { submoduleId: string }) =>
        apiRoutes.userSubmoduleCompanies(payload.submoduleId),
    }),
    // getRequestedSubModules: builder.query<RequestedSubModulesApiMap, void>({
    //   query: () => apiRoutes.fetchRequestedSubmodules(),
    // }),
    requestSubModule: builder.mutation<RequestSubModuleApiMap, string>({
      query: (requestId) => ({
        url: apiRoutes.requestSubmodule(requestId),
        method: 'PUT',
      }),
    }),
    getChartsSpecificFilterValues: builder.query<GetChartsSpecificFilterValuesApiResponse, any>({
      query: (payload: {
        submoduleId: string;
        chartId: string;
        gameId?: string;
        cohort?: string;
        model_month?: string;
        chart_view_type?: string;
        forecast_age?: string;
      }) => {
        return {
          url: apiRoutes.getChartsSpecificFilterValues(payload.submoduleId, payload.chartId),
          params: {
            ...(payload.gameId && { game_id: payload.gameId }),
            ...(payload.cohort && { cohort: payload.cohort }),
            ...(payload.model_month && { model_month: payload.model_month }),
            ...(payload.chart_view_type && { chart_view_type: payload.chart_view_type }),
            ...(payload.forecast_age && { forecast_age: payload.forecast_age }),
          },
        };
      },
    }),
    getChartsSpecificFilterValuesByGameId: builder.query<
      GetChartsSpecificFilterValuesApiResponse,
      any
    >({
      query: (payload: { submoduleId: string; chartId: string; gameId: string }) =>
        apiRoutes.getChartsSpecificFilterValuesByGameId(
          payload.submoduleId,
          payload.chartId,
          payload.gameId,
        ),
    }),
    getSubmoduleFeatureEnabled: builder.query<any, { submoduleType: string }>({
      query: ({ submoduleType }) => apiRoutes.getSubmoduleFeatureEnabled(submoduleType),
    }),
  }),
});

export const {
  useLazyGetModulesQuery,
  useLazyGetSubModulesQuery,
  useLazyGetSubModuleFiltersQuery,
  useAddFilterValuesMutation,
  useLazyGetUserModulesByIdQuery,
  useLazyGetGuestTokenQuery,
  useLazyGetCustomDashboardFiltersQuery,
  useGetChartsSpecificFilterValuesQuery,
  useLazyGetChartsSpecificFilterValuesQuery,
  useLazyGetUserModuleGamesQuery,
  useLazyGetUserModuleCompaniesQuery,
  // useLazyGetRequestedSubModulesQuery,
  useRequestSubModuleMutation,
  useLazyGetSubmoduleFeatureEnabledQuery,
  useLazyGetChartsSpecificFilterValuesByGameIdQuery,
} = modules;
